/**
 * Function to process an event firing. May optionally return true to preserve the loading state (ie for redirect).
 * @callback listenerFunction
 * @param {Event} e}
 * @returns {boolean | object} true if the loading state should be preserved (ie for redirect), or {disable: "text"} to leave disabled with new text
 */
/**
 * Connect an event handler to any and all elements tagged with data-wireup-<target>
 * @param {string} dataWireupTarget Element data tag to search for
 * @param {string} type Event handler type
 * @param {listenerFunction} listener Event handler function
 * @param {string} loadingTarget Loader element data tag to search for
 */
export function WireupHandlers(dataWireupTarget, type, listener, loadingTarget) {
    for (const element of document.querySelectorAll("[data-wireup-" + dataWireupTarget + "]")) {
        element.addEventListener(type, async (e) => {
            let loader = element;
            let preserveLoader = false;
            try {
                // We can optionally set a different target for the loading text / disable / class
                if (loadingTarget) loader = element.querySelector("[data-wireup-" + loadingTarget + "]");
                // If tagged with data-wireup-loading, we'll set the element (button) class, disabled, and content while processing the click
                if (loader.dataset["wireupLoading"]) {
                    loader.classList.add("button-loading");
                    loader.oldText = loader.innerHTML;
                    loader.setAttribute("disabled", "disabled");
                    loader.innerText = loader.dataset["wireupLoading"];
                }
                // The listener can preserve the loading state after completion if it returns true (ie for redirect)
                preserveLoader = await listener(e);
            } finally {
                if (preserveLoader && preserveLoader.disable) {
                    // Change to completed text and leave button disabled
                    loader.classList.remove("button-loading");
                    loader.innerHTML = preserveLoader.disable;
                } else if (!preserveLoader && loader.dataset["wireupLoading"]) {
                    loader.classList.remove("button-loading");
                    loader.removeAttribute("disabled");
                    if (loader.dataset["wireupLoading"]) loader.innerHTML = loader.oldText;
                }
            }
        });
    }
}

/**
 * Clears the loading state. Useful if the loader has been left active by returning true.
 * @param {string} loadingTarget Loader element data tag to search for
 * @param {boolean} loadingState true if loading, false if done
 */
export function SetLoadingState(loadingTarget, loadingState) {
    for (const loader of document.querySelectorAll("[data-wireup-" + loadingTarget + "]")) {
        if (loader.dataset["wireupLoading"]) {
            if (loadingState) {
                loader.classList.add("button-loading");
                loader.oldText = loader.innerHTML;
                loader.setAttribute("disabled", "disabled");
                loader.innerText = loader.dataset["wireupLoading"] ?? "Loading";
            } else {
                loader.classList.remove("button-loading");
                loader.removeAttribute("disabled");
                if (loader.oldText) loader.innerHTML = loader.oldText;
            }
        }
    }
}

/**
 * Sets contents of all elements tagged with data-contains-<target>
 * @param {string} dataWireupTarget Element data tag to search for
 * @param {string} content Text to fill element with
 */
export function SetTextContent(dataWireupTarget, content) {
    Array.from(document.querySelectorAll("[data-contains-" + dataWireupTarget + "]")).map(item => item.innerText = content);
}

/**
 * Show / hide any elements tagged with data-show-<target>
 * @param {string} dataWireupTarget Element data tag to search for
 */
export function SetElementsVisible(dataWireupTarget, visible = true) {
    if (visible) {
        Array.from(document.querySelectorAll("[data-show-" + dataWireupTarget + "]")).map(item => item.classList.remove("hidden"));
    } else {
        Array.from(document.querySelectorAll("[data-show-" + dataWireupTarget + "]")).map(item => item.classList.add("hidden"));
    }
}

/**
 * Disable / enable any elements tagged with data-disable-<target>
 * @param {string} dataWireupTarget Element data tag to search for
 */
export function SetElementsDisabled(dataWireupTarget, disabled = true) {
    if (disabled) {
        Array.from(document.querySelectorAll("[data-disable-" + dataWireupTarget + "]")).map(item => item.setAttribute("disabled", "disabled"));
    } else {
        Array.from(document.querySelectorAll("[data-disable-" + dataWireupTarget + "]")).map(item => item.removeAttribute("disabled"));
    }
}